import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, MenuItem, SelectField, Stack } from '@curves/ui-base';
import { Grid } from '@mui/material';
import { Overview } from '@curves/ui-shared';

import useCyclePurchaseApi from '@Apis/useCyclePurchaseApi';
import useOverview from '@Util/hook/useOverview';
import { refIsRequiredError } from '@Util/utils';
import { getCyclePurchaseVerificationBatchPaymentRowLabel } from '@Local/CyclePurchase/Components/CyclePurchaseVerificationBatchPaymentTemplate';
import { TASK_TYPE_CONFIGS } from '@Local/CyclePurchase/staticData';

const initBookmark = () => ({
    pageIndex: 1,
    pageSize: 10,
});

// 狀態「全部」，與後端選項值同為字串
const ALL_STATUS_VALUE = '99';

/**
 * 單一任務類型（扣款／退單）的總覽。
 * 外層以 key={taskType} 掛載，切換分頁即重置狀態與頁碼。
 * @param {object} props
 * @param {number} props.taskType - 任務類型（1 扣款、2 退單）
 * @param {object} props.options - 總覽選項（含各分頁的狀態下拉欄位）
 * @param {React.RefObject} props.dialogRef - CyclePurchaseDialogBox 的 ref
 * @param {React.MutableRefObject} props.refreshRef - 登記重新查詢函式，供對話框操作後使用
 */
const CyclePurchaseTaskOverview = ({ taskType, options, dialogRef, refreshRef }) => {
    const { getCyclePurchaseTasksOverviewApi } = useCyclePurchaseApi();

    // 一律帶本分頁的 taskType，不採用網址上的值
    const getOverviewApi = useCallback((params) => getCyclePurchaseTasksOverviewApi({ ...params, taskType }), [getCyclePurchaseTasksOverviewApi, taskType]);

    const { overviewData, searchParams, getOverviewData, updateOverviewPage } = useOverview({
        getOverviewApi,
        defaultSearchParams: {
            taskType,
            status: 99,
            ...initBookmark(),
        },
        isFetchOnMount: true,
    });

    const statusRef = React.useRef(null);

    // 狀態下拉採受控：選項非同步載入，初始值取自網址（重新整理後保留）
    const [status, setStatus] = useState(String(searchParams?.status ?? ALL_STATUS_VALUE));

    // 網址的狀態改變時（上一頁／下一頁）同步下拉
    useEffect(() => {
        setStatus(String(searchParams?.status ?? ALL_STATUS_VALUE));
    }, [searchParams?.status]);

    // 對話框操作完成後，以目前條件重新查詢
    useEffect(() => {
        refreshRef.current = () => getOverviewData(searchParams);
        return () => {
            refreshRef.current = null;
        };
    });

    const handleSubmit = () => {
        const isError = refIsRequiredError(statusRef);
        if (isError) return false;
        return {
            ...initBookmark(),
            taskType,
            status,
        };
    };

    const statusOptions = options?.[TASK_TYPE_CONFIGS[taskType].statusOptionKey] || [];

    const contextValue = {
        overviewData,
        searchParams,
        getOverviewData,
        updateOverviewPage,
    };

    const headerRow = useMemo(
        () => {
            return getCyclePurchaseVerificationBatchPaymentRowLabel();
        },
        // eslint-disable-next-line
        [],
    );

    return (
        <Overview contextValue={contextValue} idKey="taskID">
            <Overview.Search className="p-3 mb-1" getSearchParamsOnSubmit={handleSubmit}>
                <Grid spacing={2} container>
                    <Grid xs={8} item>
                        <SelectField
                            ref={statusRef}
                            label={'狀態'}
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            direction="row"
                            displayEmpty
                            fullWidth
                        >
                            {statusOptions.map((item) => {
                                return (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                );
                            })}
                        </SelectField>
                    </Grid>
                    <Grid xs={4} item>
                        <Stack className={'h-100'} alignItems={'center'}>
                            <Button type={'submit'} variant={'contained'} color="primary">
                                搜尋
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Overview.Search>
            <Overview.Table headerRow={headerRow} cellRenderContext={{ dialogRef, refresh: () => getOverviewData(searchParams) }} />
            <Overview.Pagination className="mt-4" />
        </Overview>
    );
};

export default CyclePurchaseTaskOverview;
